export default {
  methods: {
    /**
     * Extract error message from various error formats
     * Handles: Axios errors, plain strings, Error objects, API response formats
     */
    extractErrorMessage(error) {
      // If it's already a string, return it
      if (typeof error === 'string') {
        return error
      }

      // Handle null/undefined
      if (error == null) {
        return 'An unexpected error occurred. Please try again.'
      }

      // Handle Axios error responses
      if (error.response) {
        const data = error.response.data
        const status = error.response.status

        // Handle permission/authorization errors (403)
        if (status === 403) {
          if (data.permission_required) {
            return `You don't have permission to perform this action. Required: ${this.formatPermissionName(data.permission_required)}`
          }
          if (data.error) {
            return data.error
          }
          return 'You don\'t have permission to perform this action.'
        }

        // Handle not found errors (404)
        if (status === 404) {
          return data.error || 'The requested resource was not found.'
        }

        // Handle validation errors (422)
        if (status === 422) {
          if (data.errors) {
            // Rails validation errors format: { errors: { field: ["message"] } }
            if (typeof data.errors === 'object' && !Array.isArray(data.errors)) {
              const messages = []
              for (const field in data.errors) {
                const fieldName = this.formatFieldName(field)
                const fieldErrors = data.errors[field]
                if (Array.isArray(fieldErrors)) {
                  fieldErrors.forEach(msg => messages.push(`${fieldName} ${msg}`))
                } else {
                  messages.push(`${fieldName} ${fieldErrors}`)
                }
              }
              return messages.join('. ')
            }
            // Array of error strings
            if (Array.isArray(data.errors)) {
              return data.errors.join('. ')
            }
            return data.errors
          }
          if (data.error) {
            return data.error
          }
          return 'Please check your input and try again.'
        }

        // Handle server errors (500)
        if (status >= 500) {
          return data.error || 'Server error. Please try again later.'
        }

        // Handle unauthorized (401)
        if (status === 401) {
          return data.error || 'Please log in to continue.'
        }

        // Generic response with error field
        if (data.error) {
          return data.error
        }

        // Generic response with message field
        if (data.message) {
          return data.message
        }

        // Array of errors
        if (data.errors && Array.isArray(data.errors)) {
          return data.errors.join('. ')
        }
      }

      // Handle network errors
      if (error.request && !error.response) {
        return 'Network error. Please check your connection and try again.'
      }

      // Handle standard Error objects
      if (error.message) {
        // Don't show technical error messages to users
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          return 'Network error. Please check your connection and try again.'
        }
        // For other error messages, check if they're user-friendly
        if (!error.message.includes('Error:') && !error.message.includes('at ')) {
          return error.message
        }
      }

      return 'An unexpected error occurred. Please try again.'
    },

    /**
     * Format permission name for display
     * e.g., "manage_products" -> "Manage Products"
     */
    formatPermissionName(permission) {
      if (!permission) return ''
      return permission
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    /**
     * Format field name for display
     * e.g., "first_name" -> "First name"
     */
    formatFieldName(field) {
      if (!field) return ''
      return field
        .split('_')
        .map((word, index) => index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
        .join(' ')
    },

    /**
     * Show error notification
     * Accepts: string, Error object, or Axios error
     */
    showError(error) {
      const message = this.extractErrorMessage(error)
      this.$notify({
        title: 'Error',
        text: message,
        type: 'error',
        duration: 5500
      });
    },

    /**
     * Show permission denied error with specific permission info
     */
    showPermissionError(permission) {
      const permissionName = this.formatPermissionName(permission)
      this.$notify({
        title: 'Permission Denied',
        text: `You don't have permission to ${permissionName.toLowerCase()}.`,
        type: 'error',
        duration: 5500
      });
    },

    /**
     * Show success notification
     */
    showSuccess(success) {
      this.$notify({
        title: 'Success',
        text: success,
        type: 'success',
        duration: 3000
      });
    },

    /**
     * Show warning notification
     */
    showWarning(warning) {
      this.$notify({
        title: 'Warning',
        text: warning,
        type: 'warn',
        duration: 4000
      });
    },

    /**
     * Show info notification
     */
    showInfo(info) {
      this.$notify({
        title: 'Info',
        text: info,
        type: 'info',
        duration: 3000
      });
    }
  }
}
